import styled from 'styled-components';
import type {
  ShareFiltersInput,
  ShareOrderFieldEnum,
} from '@frameio/graphql-client/generated';
import { useGetActiveUserId } from '@frameio/shared/hooks/useGetActiveUserId';
import {
  Box,
  MenuButton,
  MenuGroup,
  MenuRadio,
  MenuSeparator,
  Stack,
  Text,
  theme,
} from '@frameio/vapor';
import { IconSortArrows } from '@frameio/vapor-icons';
import TEXT from './generated/TEXT/SharesMenuContent';

export type ShareFilterEnum = 'ALL' | 'CREATED_BY_USER';

type SharesMenuContentProps = {
  sortBy: {
    field: ShareOrderFieldEnum;
    order: 'ASC' | 'DESC';
  };
  filterBy: ShareFiltersInput;
  onSortByFieldSelect: (field: ShareOrderFieldEnum) => void;
  onFilterByFieldSelect: (value: ShareFiltersInput) => void;
  onToggleSortByOrder: () => void;
};

export const SharesMenuContent = ({
  sortBy,
  filterBy,
  onSortByFieldSelect,
  onFilterByFieldSelect,
  onToggleSortByOrder,
}: SharesMenuContentProps) => {
  const activeUserId = useGetActiveUserId();
  const getFilterCheckedState = (value: ShareFilterEnum) => {
    if (value === 'CREATED_BY_USER') {
      return filterBy.creatorId === activeUserId;
    }

    if (value === 'ALL') {
      return filterBy.creatorId === undefined;
    }

    return false;
  };
  return (
    <>
      <MenuGroup label={TEXT.sortBy}>
        <Stack gap={2}>
          {Object.entries(SORT_BY_FIELD_OPTIONS).map(([value, label]) => (
            <MenuRadio
              key={label}
              checked={value === sortBy.field}
              name={value}
              onPress={() => onSortByFieldSelect(value as ShareOrderFieldEnum)}
            >
              {label}
            </MenuRadio>
          ))}
          <MenuSeparator />
          <StyledOrderByMenuButton
            data-testid="sort-by-order-button"
            onClick={onToggleSortByOrder}
            shouldCloseMenuOnSelect={false}
            solidBackground={false}
          >
            <Box
              display="inline-flex"
              justifyContent="space-between"
              width="100%"
            >
              <Text color="primary" variant="body">
                {TEXT.orderBy}
              </Text>
              <Box display="inline-flex" gap={4}>
                <Text color="secondary" variant="body">
                  {
                    TEXT.sortByOrderLabel[
                      sortBy.field as keyof typeof TEXT.sortByOrderLabel
                    ][sortBy.order]
                  }
                </Text>
                <IconAfter>
                  <IconSortArrows color={theme.colors.icon.primary} size={16} />
                </IconAfter>
              </Box>
            </Box>
          </StyledOrderByMenuButton>
        </Stack>
      </MenuGroup>
      <MenuGroup label={TEXT.filterBy}>
        <Stack gap={2}>
          {Object.entries(FILTER_BY_OPTIONS).map(([value, label]) => (
            <MenuRadio
              key={value}
              checked={getFilterCheckedState(value as ShareFilterEnum)}
              name={value}
              onPress={() => {
                const filters: ShareFiltersInput = {};
                if (value === 'CREATED_BY_USER') {
                  filters.creatorId = activeUserId;
                }
                if (value === 'ALL') {
                  filters.creatorId = undefined;
                }
                onFilterByFieldSelect(filters);
              }}
            >
              {label}
            </MenuRadio>
          ))}
        </Stack>
      </MenuGroup>
    </>
  );
};

const StyledOrderByMenuButton = styled(MenuButton)`
  align-items: center;
  display: inline-flex;
  flex-grow: 1;
  justify-content: space-between;
  width: 100%;
`;

const IconAfter = styled.div`
  align-items: center;
  display: flex;
`;

TEXT.DEFINE({
  /** Menu group label for sorting shares */
  sortBy: 'Sort By',
  /** Label for the sort order toggle */
  orderBy: 'Order By',
  /** Menu group label for filtering shares */
  filterBy: 'Filter By',
  /** Labels for sort-by fields and order values */
  sortByOrderLabel: {
    LAST_VIEWED_AT: {
      /** Label for last viewed sort field */
      label: 'Latest Activity',
      /** Ascending label for last viewed sort */
      ASC: 'Oldest',
      /** Descending label for last viewed sort */
      DESC: 'Latest',
    },
    VIEW_COUNT: {
      /** Label for view count sort field */
      label: 'View Count',
      /** Ascending label for view count sort */
      ASC: 'Least',
      /** Descending label for view count sort */
      DESC: 'Most',
    },
    INSERTED_AT: {
      /** Label for created date sort field */
      label: 'Date Created',
      /** Ascending label for created date sort */
      ASC: 'Oldest',
      /** Descending label for created date sort */
      DESC: 'Newest',
    },
    UPDATED_AT: {
      /** Label for updated date sort field */
      label: 'Date Updated',
      /** Ascending label for updated date sort */
      ASC: 'Oldest',
      /** Descending label for updated date sort */
      DESC: 'Newest',
    },
    NAME: {
      /** Label for name sort field */
      label: 'Alphabetical',
      /** Ascending label for name sort */
      ASC: 'A → Z',
      /** Descending label for name sort */
      DESC: 'Z → A',
    },
  },
  /** Labels for filter-by options */
  filterByLabel: {
    /** Filter label for all shares */
    ALL: 'All Shares',
    /** Filter label for shares created by the user */
    CREATED_BY_USER: 'Created by You',
  },
});

export const SORT_BY_FIELD_OPTIONS: {
  [key in ShareOrderFieldEnum]?: string;
} = {
  UPDATED_AT: TEXT.sortByOrderLabel.UPDATED_AT.label,
  LAST_VIEWED_AT: TEXT.sortByOrderLabel.LAST_VIEWED_AT.label,
  VIEW_COUNT: TEXT.sortByOrderLabel.VIEW_COUNT.label,
  INSERTED_AT: TEXT.sortByOrderLabel.INSERTED_AT.label,
  NAME: TEXT.sortByOrderLabel.NAME.label,
};

export const FILTER_BY_OPTIONS: Record<ShareFilterEnum, string> = {
  ALL: TEXT.filterByLabel.ALL,
  CREATED_BY_USER: TEXT.filterByLabel.CREATED_BY_USER,
};
